'use client'

import { ReactNode, useMemo, CSSProperties } from 'react'
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GearIcon } from '@phosphor-icons/react/dist/ssr/Gear'
import { DotsSixVerticalIcon } from '@phosphor-icons/react/dist/ssr/DotsSixVertical'

import { Button } from '@/components/atoms/Button/button.atom'
import { Checkbox } from '@/components/atoms/Checkbox/checkbox.atom'
import { DropdownMenu } from '@/components/atoms/DropdownMenu/dropdownMenu.atom'

type SortableItemProps = {
  id: string
  onToggleColumnVisibility: (id: string, next: boolean) => void
  getColumnLabel: (id: string) => string
  columnVisibility: Record<string, boolean>
}

const SortableItem = ({
  id,
  onToggleColumnVisibility,
  getColumnLabel,
  columnVisibility,
}: SortableItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  }
  const checked = columnVisibility[id] ?? true
  return (
    <div
      ref={setNodeRef}
      style={style}
      className='flex items-center justify-between gap-2 rounded px-2 py-1.5 hover:bg-neutral-100'
    >
      <div className='flex items-center gap-2'>
        <Checkbox
          name={`toggle-${id}`}
          checked={checked}
          onChange={(e) =>
            onToggleColumnVisibility(
              id,
              (e?.target as HTMLInputElement).checked,
            )
          }
          aria-label={`Afficher la colonne ${id}`}
        />
        <span className='text-sm text-slate-500'>{getColumnLabel(id)}</span>
      </div>
      <button
        aria-label='drag-handle'
        className='cursor-grab p-1 text-slate-300'
        {...attributes}
        {...listeners}
      >
        <DotsSixVerticalIcon className='size-4' />
      </button>
    </div>
  )
}

type ColumnsManagerProps = {
  table: any
  columnOrder: string[]
  onColumnOrderChange: (next: string[]) => void
  fixedColumnIds: string[]
  columnVisibility: Record<string, boolean>
  onToggleColumnVisibility: (id: string, next: boolean) => void
  columnsBtnTitle?: string
  columnsBtnIcon?: ReactNode
}

export const ColumnsManager = (props: ColumnsManagerProps) => {
  const {
    table,
    columnOrder,
    onColumnOrderChange,
    fixedColumnIds,
    columnVisibility,
    onToggleColumnVisibility,
    columnsBtnTitle,
    columnsBtnIcon = <GearIcon className='size-5 text-slate-300' />,
  } = props

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  )

  const activeManagedIds = useMemo(
    () =>
      columnOrder.filter(
        (id) =>
          id !== 'select' &&
          !fixedColumnIds.includes(id) &&
          table.getColumn(id),
      ),
    [columnOrder, fixedColumnIds, table],
  )

  const isDisplayableColumn = (id: string) => {
    const col = table.getColumn(id)
    // Consider a column displayable if it exists,
    // regardless of header type (string, ReactNode, or function)
    return !!col
  }

  // Get the label of a column
  const getColumnLabel = (id: string) => {
    const col = table.getColumn(id)
    const columnDef = col?.columnDef

    if (columnDef?.meta?.label) {
      return columnDef.meta.label
    }

    const header = columnDef?.header
    if (typeof header === 'string') {
      return header.trim().length > 0 ? header : id
    }

    if (typeof header === 'function') {
      try {
        return columnDef?.accessorKey || id
      } catch {
        return id
      }
    }

    if (header && typeof header === 'object') {
      return columnDef?.accessorKey || id
    }

    return columnDef?.accessorKey || id
  }

  const handleDragEnd = (event: any) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = activeManagedIds.indexOf(active.id)
    const newIndex = activeManagedIds.indexOf(over.id)
    if (oldIndex === -1 || newIndex === -1) return
    const reorderedManaged = arrayMove(activeManagedIds, oldIndex, newIndex)
    let managedCursor = 0
    const next = columnOrder.map((id) => {
      if (id === 'select' || fixedColumnIds.includes(id)) return id
      if (activeManagedIds.includes(id)) {
        const nextId = reorderedManaged[managedCursor]
        managedCursor += 1
        return nextId
      }
      return id
    })
    onColumnOrderChange(next)
  }

  return (
    <DropdownMenu.Root>
      {(columnsBtnIcon || columnsBtnTitle) && (
        <DropdownMenu.Trigger asChild>
          <Button variant='outline' className='flex items-center gap-2'>
            {columnsBtnIcon}
            {columnsBtnTitle}
          </Button>
        </DropdownMenu.Trigger>
      )}
      <DropdownMenu.Content align='end' className='bg-white w-72'>
        <div className='p-2'>
          <div className='p-2 text-xs font-medium text-slate-300'>
            Colonnes fixes
          </div>
          <div className='flex flex-col gap-1 pb-2'>
            {fixedColumnIds.filter(isDisplayableColumn).length === 0 && (
              <div className='px-2 text-sm text-slate-300'>Aucune</div>
            )}
            {fixedColumnIds.filter(isDisplayableColumn).map((id) => (
              <div key={id} className='px-2 py-1.5 text-sm text-slate-500'>
                {getColumnLabel(id)}
              </div>
            ))}
          </div>
          <DropdownMenu.Separator className='my-1' />
          <div className='p-2 text-xs font-medium text-slate-300'>
            Colonnes actives
          </div>
          <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
            <SortableContext
              items={activeManagedIds.filter(isDisplayableColumn)}
              strategy={verticalListSortingStrategy}
            >
              <div className='flex flex-col gap-1'>
                {activeManagedIds.filter(isDisplayableColumn).map((id) => (
                  <SortableItem
                    key={id}
                    id={id}
                    onToggleColumnVisibility={onToggleColumnVisibility}
                    getColumnLabel={getColumnLabel}
                    columnVisibility={columnVisibility}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}
